import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Copy, Loader2 } from "lucide-react";
import { AppShell, type AppShellContext } from "@/components/app/AppShell";
import { Button } from "@/components/ui/button";
import {
  amazonTakedownCapability,
  amazonTakedownCapabilityLabel,
  type AmazonTakedownCapability,
  type BrandAmazonAccess,
} from "@/lib/amazonAccess";
import { api } from "@/lib/api";

const AGENT_EMAIL = "agent@reputely.co";

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch {
          // Clipboard access may be unavailable in some browsers.
        }
      }}
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? "Copied" : "Copy"}
    </Button>
  );
}

function capabilityPill(capability: AmazonTakedownCapability) {
  if (capability === "brand_registry_ready") {
    return "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900";
  }
  if (capability === "brand_registry_access_pending") {
    return "bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-900";
  }
  if (capability === "unverified_no_brand_registry") {
    return "bg-amber-50 text-amber-800 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900";
  }
  return "bg-slate-100 text-slate-600 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700";
}

function CapabilityPill({ brand }: { brand: BrandAmazonAccess }) {
  const capability = amazonTakedownCapability(brand);
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${capabilityPill(capability)}`}
    >
      {amazonTakedownCapabilityLabel(capability)}
    </span>
  );
}

function Inner({ ctx }: { ctx: AppShellContext }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [brands, setBrands] = useState<BrandAmazonAccess[]>(ctx.brands);
  const requestedBrandId = searchParams.get("brand");
  const selectedBrand = useMemo(
    () =>
      brands.find((brand) => brand.id === requestedBrandId) ??
      brands.find((brand) => brand.id === ctx.activeBrandId) ??
      brands[0],
    [brands, ctx.activeBrandId, requestedBrandId]
  );

  function selectBrand(id: string) {
    setSearchParams({ brand: id });
  }

  function updateBrand(updated: BrandAmazonAccess) {
    setBrands((current) =>
      current.map((brand) => (brand.id === updated.id ? updated : brand))
    );
  }

  if (!selectedBrand) {
    return (
      <div className="mx-auto w-full max-w-2xl px-6 py-16 text-center">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
          Add a brand before configuring Amazon
        </h1>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          Monitoring and takedown access are tracked separately for each brand.
        </p>
        <Link
          to="/dashboard/brands/new"
          className="mt-6 inline-flex rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
        >
          Add a brand
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-6 py-8">
      <Link
        to="/dashboard"
        className="text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
      >
        ← Back to dashboard
      </Link>
      <h1 className="mt-5 text-3xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
        Amazon setup
      </h1>
      <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
        Confirm monitoring and managed-takedown coverage for each brand.
      </p>

      {brands.length > 1 && (
        <div className="mt-6">
          <label
            htmlFor="onboarding-brand"
            className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400"
          >
            Brand
          </label>
          <select
            id="onboarding-brand"
            value={selectedBrand.id}
            onChange={(event) => selectBrand(event.target.value)}
            className="mt-2 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
          >
            {brands.map((brand) => (
              <option key={brand.id} value={brand.id}>
                {brand.name} — {amazonTakedownCapabilityLabel(amazonTakedownCapability(brand))}
              </option>
            ))}
          </select>
        </div>
      )}

      <BrandOnboarding
        key={selectedBrand.id}
        brand={selectedBrand}
        onUpdate={updateBrand}
      />
    </div>
  );
}

function BrandOnboarding({
  brand,
  onUpdate,
}: {
  brand: BrandAmazonAccess;
  onUpdate: (brand: BrandAmazonAccess) => void;
}) {
  const [current, setCurrent] = useState(brand);
  const [editingRegistry, setEditingRegistry] = useState(
    amazonTakedownCapability(brand) === "unknown"
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const capability = amazonTakedownCapability(current);

  async function updateAccess(body: {
    brandRegistry?: boolean;
    collaboratorStatus?: "none" | "invite_sent";
  }) {
    const res = await api<{ brand: BrandAmazonAccess }>(
      `/customer/brands/${current.id}/collaborator-status`,
      {
        method: "PATCH",
        body: JSON.stringify(body),
      }
    );
    if (!res.ok || !res.data?.brand) {
      throw new Error(res.error ?? "Could not update Amazon access.");
    }
    setCurrent(res.data.brand);
    onUpdate(res.data.brand);
    return res.data.brand;
  }

  async function saveBrandRegistry(value: boolean) {
    setError(null);
    setSubmitting(true);
    try {
      await updateAccess({ brandRegistry: value });
      setEditingRegistry(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save your answer.");
    } finally {
      setSubmitting(false);
    }
  }

  async function markInviteSent() {
    setError(null);
    setSubmitting(true);
    try {
      await updateAccess({ collaboratorStatus: "invite_sent" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update your status.");
    } finally {
      setSubmitting(false);
    }
  }

  if (editingRegistry) {
    return (
      <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-8 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
          {current.name}
        </div>
        <h2 className="mt-2 text-xl font-semibold text-slate-900 dark:text-slate-100">
          Is this brand enrolled in Amazon Brand Registry?
        </h2>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          Monitoring does not require Brand Registry. Reputely-managed takedown filing currently does.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button onClick={() => saveBrandRegistry(true)} disabled={submitting}>
            {submitting && <Loader2 className="animate-spin" />}
            Yes, it is enrolled
          </Button>
          <Button
            variant="outline"
            onClick={() => saveBrandRegistry(false)}
            disabled={submitting}
          >
            No, it is not enrolled
          </Button>
        </div>
        {error && <p className="mt-4 text-sm text-destructive" role="alert">{error}</p>}
      </section>
    );
  }

  if (capability === "unverified_no_brand_registry") {
    return (
      <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-8 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-sm font-medium text-slate-500 dark:text-slate-400">{current.name}</div>
            <h2 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
              Monitoring is available. Managed takedowns are not yet available.
            </h2>
          </div>
          <CapabilityPill brand={current} />
        </div>
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl bg-emerald-50 p-4 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950/40 dark:ring-emerald-900">
            <div className="text-xs text-emerald-700 dark:text-emerald-300">Review monitoring</div>
            <div className="mt-1 font-medium text-emerald-900 dark:text-emerald-200">Available without Amazon account access</div>
          </div>
          <div className="rounded-xl bg-amber-50 p-4 ring-1 ring-inset ring-amber-200 dark:bg-amber-950/40 dark:ring-amber-900">
            <div className="text-xs text-amber-700 dark:text-amber-300">Reputely-managed takedowns</div>
            <div className="mt-1 font-medium text-amber-900 dark:text-amber-200">Not yet supported for this brand</div>
          </div>
        </div>
        <div className="mt-6 rounded-xl bg-slate-50 p-5 text-sm text-slate-700 dark:bg-slate-950 dark:text-slate-300">
          <h3 className="font-medium text-slate-900 dark:text-slate-100">What Amazon makes available</h3>
          <ul className="mt-3 space-y-2">
            <li><strong>Public review report:</strong> anyone can report one review from its product page. Amazon decides whether to remove it.</li>
            <li><strong>Seller Central Report Abuse:</strong> Amazon documents this for sellers, but Reputely has not verified delegated Authorized Partner access end to end.</li>
            <li><strong>Reputely managed filing:</strong> our verified batch workflow currently uses Brand Registry Support.</li>
          </ul>
        </div>
        {current.collaboratorStatus !== "none" && (
          <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
            Previous Amazon access history is preserved ({current.collaboratorStatus.replace("_", " ")}), but it does not make this brand takedown-ready.
          </p>
        )}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Link
            to="/dashboard"
            className="inline-flex rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
          >
            Continue to dashboard
          </Link>
          <button
            type="button"
            onClick={() => setEditingRegistry(true)}
            className="text-sm text-slate-500 underline-offset-4 hover:underline dark:text-slate-400"
          >
            Update Brand Registry answer
          </button>
        </div>
      </section>
    );
  }

  if (
    capability === "brand_registry_access_pending" ||
    capability === "brand_registry_ready"
  ) {
    const ready = capability === "brand_registry_ready";
    return (
      <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-8 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-sm font-medium text-slate-500 dark:text-slate-400">{current.name}</div>
            <h2 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
              {ready ? "Managed takedowns are ready." : "Invitation sent — we're confirming access."}
            </h2>
          </div>
          <CapabilityPill brand={current} />
        </div>
        <p className="mt-4 text-sm text-slate-600 dark:text-slate-400">
          {ready
            ? `Reputely has verified Brand Registry access for ${current.name}.`
            : "We'll accept the Brand Registry invitation and verify the Registered Agent role within 24 hours."}
        </p>
        <dl className="mt-5 grid gap-3 text-sm sm:grid-cols-2">
          <div className="rounded-xl bg-slate-50 p-4 dark:bg-slate-950">
            <dt className="text-xs text-slate-500 dark:text-slate-400">Review monitoring</dt>
            <dd className="mt-1 font-medium text-slate-900 dark:text-slate-100">Available</dd>
          </div>
          <div className="rounded-xl bg-slate-50 p-4 dark:bg-slate-950">
            <dt className="text-xs text-slate-500 dark:text-slate-400">{ready ? "Access verified" : "Invitation sent"}</dt>
            <dd className="mt-1 font-medium text-slate-900 dark:text-slate-100">
              {new Date((ready ? current.verifiedAt : current.inviteSentAt) ?? Date.now()).toLocaleString()}
            </dd>
          </div>
        </dl>
        <button
          type="button"
          onClick={() => setEditingRegistry(true)}
          className="mt-6 text-sm text-slate-500 underline-offset-4 hover:underline dark:text-slate-400"
        >
          Update Brand Registry answer
        </button>
      </section>
    );
  }

  return (
    <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-8 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-slate-500 dark:text-slate-400">{current.name}</div>
          <h2 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
            Invite Reputely to Amazon Brand Registry
          </h2>
        </div>
        <CapabilityPill brand={current} />
      </div>
      <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
        Assign the Registered Agent protection role—the Amazon role for a third party authorized by the Rights Owner to use Brand Registry protection tools.
      </p>
      <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-950">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">Email to invite</div>
            <div className="mt-1 font-mono text-sm font-medium text-slate-900 dark:text-slate-100">{AGENT_EMAIL}</div>
          </div>
          <CopyButton text={AGENT_EMAIL} />
        </div>
      </div>
      <ol className="mt-6 space-y-4 text-sm text-slate-700 dark:text-slate-300">
        <li><strong>1.</strong> Sign in to <a href="https://brandregistry.amazon.com/" target="_blank" rel="noreferrer" className="underline underline-offset-4">Amazon Brand Registry</a>.</li>
        <li><strong>2.</strong> Open the gear menu, choose <strong>User Permissions</strong>, then <strong>Invite a user to your brand</strong>.</li>
        <li><strong>3.</strong> Enter <span className="font-mono text-xs">{AGENT_EMAIL}</span>, choose this brand and marketplace, and assign the <strong>Registered Agent</strong> protection role.</li>
        <li><strong>4.</strong> Send the invitation.</li>
      </ol>
      <p className="mt-5 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-xs text-sky-900 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-200">
        Do not invite Reputely through Seller Central&rsquo;s User Permissions. Amazon requires outside service providers to use Authorized Partners when Seller Central access is needed. Reputely is not requesting Seller Central access in this setup.
      </p>
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Button onClick={markInviteSent} disabled={submitting}>
          {submitting && <Loader2 className="animate-spin" />}
          I&rsquo;ve sent the Brand Registry invitation
        </Button>
        <button
          type="button"
          onClick={() => setEditingRegistry(true)}
          className="text-sm text-slate-500 underline-offset-4 hover:underline dark:text-slate-400"
        >
          Change answer
        </button>
      </div>
      {error && <p className="mt-4 text-sm text-destructive" role="alert">{error}</p>}
    </section>
  );
}

export default function CustomerOnboarding() {
  return <AppShell>{(ctx) => <Inner ctx={ctx} />}</AppShell>;
}
